import * as THREE from 'three';
import type { TargetKindId } from '../config/targets';
import { Rng } from '../core/Rng';
import type { TargetInstance } from '../combat/TargetField';
import { createTargetGeometry, targetColour } from './generators/TargetGen';

/**
 * Draws the targets (SPEC §5.1).
 *
 * One geometry per *type*, shared across instances, and one mesh per instance — there are only
 * ever a handful of targets in a theatre, so instancing would add complexity for nothing. What
 * matters here is that a destroyed target visibly *becomes* wreckage: the player's only feedback
 * that a run worked is what they see in the last second before the screen goes dark, and a target
 * that looks identical after being hit reads as a bug.
 */

const WRECK_COLOUR = new THREE.Color(0x2a2724);

export class TargetRenderer {
  readonly object3d = new THREE.Group();

  private readonly geometries = new Map<TargetKindId, THREE.BufferGeometry>();
  private readonly meshes: THREE.Mesh[] = [];
  private readonly materials: THREE.MeshLambertMaterial[] = [];
  private readonly intactColours: THREE.Color[] = [];

  private readonly seed: number;

  constructor(targets: readonly TargetInstance[], seed: number) {
    this.seed = seed;
    this.build(targets);
  }

  /**
   * Replace every mesh for a new mission's targets.
   *
   * Geometries are cached by kind and deliberately *not* disposed here: the eight archetypes are
   * generated once and shared across every mission, so regenerating them per mission would be
   * pure waste. Materials are per-instance (each carries its own scorch colour) and are disposed.
   */
  rebuild(targets: readonly TargetInstance[]): void {
    for (let i = 0; i < this.meshes.length; i += 1) {
      this.object3d.remove(this.meshes[i]);
      this.materials[i].dispose();
    }
    this.meshes.length = 0;
    this.materials.length = 0;
    this.intactColours.length = 0;
    this.build(targets);
  }

  private build(targets: readonly TargetInstance[]): void {
    const rng = new Rng(this.seed);

    for (let i = 0; i < targets.length; i += 1) {
      const target = targets[i];
      let geometry = this.geometries.get(target.params.id);
      if (geometry === undefined) {
        geometry = createTargetGeometry(target.params.id);
        this.geometries.set(target.params.id, geometry);
      }

      const colour = targetColour(target.params.id, rng);
      const material = new THREE.MeshLambertMaterial({ color: colour, fog: true });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.copy(target.positionM);
      mesh.rotation.y = target.headingRad;
      this.object3d.add(mesh);

      this.meshes.push(mesh);
      this.materials.push(material);
      this.intactColours.push(colour.clone());
    }
  }

  /**
   * Reflect destruction. Called when a target's state changes, not every frame.
   *
   * A destroyed target darkens to scorched wreckage and settles — it does not vanish, because a
   * hole where the target was would rob the player of the one moment of feedback the whole run
   * was for.
   */
  update(targets: readonly TargetInstance[]): void {
    for (let i = 0; i < targets.length && i < this.meshes.length; i += 1) {
      const target = targets[i];
      const material = this.materials[i];

      if (target.destroyed) {
        material.color.copy(WRECK_COLOUR);
        // Settle into the ground and lean over: a wreck, not a model with a different texture.
        this.meshes[i].position.y = target.positionM.y - 0.6;
        this.meshes[i].rotation.z = 0.14;
      } else if (target.integrity < 1) {
        // Degraded: scorched toward the wreck colour in proportion to the damage taken.
        material.color.copy(this.intactColours[i]).lerp(WRECK_COLOUR, 1 - target.integrity);
      }
    }
  }

  dispose(): void {
    for (const geometry of this.geometries.values()) geometry.dispose();
    for (const material of this.materials) material.dispose();
  }
}
